import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Public by design (site key + Worker URL are not secrets). Overridable at build time.
const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAADj1-LrESZ_JrLq-";
const SUBMISSIONS_API_URL =
  process.env.REACT_APP_SUBMISSIONS_API_URL ||
  "https://sajilohealth-early-access.refiectiveryanspk99.workers.dev/api/submissions";

// Client role → API role. clinic/hospital both map to the Worker's clinic_hospital;
// doctor is known-good; investor is additive for deck requests.
const ROLE_API_MAP = {
  clinic: "clinic_hospital",
  hospital: "clinic_hospital",
  doctor: "doctor",
  investor: "investor",
};

export default function DemoForm() {
  const { t } = useLanguage();
  const d = t.demo;

  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({ email: "", role: "", turnstile: "" });
  const [turnstileToken, setTurnstileToken] = useState("");

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || done) return;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current) return;
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      turnstileRef.current.innerHTML = "";
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        appearance: "always",
        callback: (token) => {
          setTurnstileToken(token);
          setErrors((prev) => ({ ...prev, turnstile: "" }));
        },
        "expired-callback": () => setTurnstileToken(""),
        "timeout-callback": () => setTurnstileToken(""),
        "error-callback": () => {
          setTurnstileToken("");
          setErrors((prev) => ({ ...prev, turnstile: d.errVerify }));
        },
      });
    };

    if (window.turnstile && window.turnstile.render) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile && window.turnstile.render) {
          clearInterval(interval);
          render();
        }
      }, 150);
      setTimeout(() => clearInterval(interval), 10000);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
        /* ignore */
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = { email: "", role: "", turnstile: "" };
    if (!EMAIL_RE.test(email.trim())) next.email = d.errEmail;
    if (!role) next.role = d.errRole;
    if (TURNSTILE_SITE_KEY && !turnstileToken) next.turnstile = d.errVerify;
    setErrors(next);
    if (next.email || next.role || next.turnstile) return;

    setSubmitting(true);

    const payload = {
      name: name.trim(),
      organization: org.trim(),
      email: email.trim(),
      role: ROLE_API_MAP[role] || role,
      "cf-turnstile-response": turnstileToken,
      turnstileToken,
    };

    (async () => {
      try {
        const res = await fetch(SUBMISSIONS_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        });

        let body = null;
        try {
          body = await res.json();
        } catch (e) {
          body = null;
        }

        if (!res.ok) {
          const message = (body && (body.error || body.message)) || d.errGeneric;
          throw new Error(message);
        }

        setSubmitting(false);
        setDone(true);
        toast.success(d.success, { description: d.successDesc });
      } catch (err) {
        const isNetwork =
          err instanceof TypeError ||
          (err && /failed to fetch|networkerror/i.test(String(err.message)));
        const message = isNetwork ? d.errNetwork : err.message || d.errGeneric;
        setSubmitting(false);
        toast.error(message);
        resetTurnstile();
      }
    })();
  };

  const inputBase =
    "mt-2 w-full px-4 py-3.5 rounded-xl border bg-white text-ink placeholder:text-textsoft/50 text-[14px] transition-all focus:outline-none focus:ring-2 focus:ring-green-soft focus:border-green";

  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="relative py-24 sm:py-32 border-t border-ink-line text-white overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0F2A47 0%, #0A2138 100%)" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(55% 45% at 50% 0%, rgba(63,158,114,0.16), transparent 60%)" }}
      />

      <div className="relative shell">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="kicker text-green">{d.kicker}</span>
            <h2 data-testid="demo-headline" className="mt-5 font-display text-[30px] sm:text-[40px] lg:text-[44px] font-semibold tracking-tight leading-[1.1]">
              {d.h2}
            </h2>
            <p className="mt-5 text-[16px] text-white/60 leading-[1.65] max-w-[440px]">{d.sub}</p>

            <div className="mt-7 inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-white/[0.05] border border-white/10">
              <span className="live-dot" />
              <span className="text-[12px] font-medium text-white/75 tracking-wide">{d.note}</span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white text-ink rounded-[22px] p-7 sm:p-9 border border-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.5)]">
              {done ? (
                <div data-testid="demo-success-state" className="flex flex-col items-start gap-4 py-8">
                  <span className="inline-flex w-12 h-12 items-center justify-center rounded-2xl bg-green-soft text-green">
                    <CheckCircle2 strokeWidth={1.8} size={26} />
                  </span>
                  <div>
                    <div className="font-display text-[24px] font-semibold text-ink tracking-tight">{d.success}</div>
                    <div className="mt-2 text-[14px] text-textsoft max-w-md leading-[1.6]">{d.successDesc}</div>
                  </div>
                  <button
                    type="button"
                    data-testid="demo-submit-another"
                    onClick={() => {
                      setDone(false);
                      setName("");
                      setOrg("");
                      setEmail("");
                      setRole("");
                      resetTurnstile();
                    }}
                    className="mt-2 text-[13px] font-semibold text-green hover:text-green-dark"
                  >
                    {d.another}
                  </button>
                </div>
              ) : (
                <form data-testid="demo-form" onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="demo-name" className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-textsoft">{d.name}</label>
                      <input id="demo-name" data-testid="demo-name-input" type="text" autoComplete="name" placeholder={d.namePh} value={name} onChange={(e) => setName(e.target.value)} className={`${inputBase} border-line`} />
                    </div>
                    <div>
                      <label htmlFor="demo-org" className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-textsoft">{d.org}</label>
                      <input id="demo-org" data-testid="demo-org-input" type="text" autoComplete="organization" placeholder={d.orgPh} value={org} onChange={(e) => setOrg(e.target.value)} className={`${inputBase} border-line`} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="demo-email" className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-textsoft">{d.email}</label>
                    <input id="demo-email" data-testid="demo-email-input" type="email" autoComplete="email" placeholder={d.emailPh} value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputBase} ${errors.email ? "border-rose-300" : "border-line"}`} />
                    {errors.email && <p data-testid="demo-email-error" className="mt-2 text-[12px] text-rose-600">{errors.email}</p>}
                  </div>

                  <div>
                    <span className="block text-[11px] uppercase tracking-[0.16em] font-semibold text-textsoft">{d.roleLabel}</span>
                    <div role="radiogroup" aria-label={d.roleLabel} className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {d.roles.map((opt) => {
                        const active = role === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            data-testid={`demo-role-${opt.id}`}
                            onClick={() => setRole(opt.id)}
                            className={`px-3 py-3 rounded-xl border text-[13px] font-semibold transition-all ${
                              active ? "border-green bg-green-soft text-ink ring-2 ring-green-soft" : "border-line bg-white text-textsoft hover:border-line-strong"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.role && <p data-testid="demo-role-error" className="mt-2 text-[12px] text-rose-600">{errors.role}</p>}
                  </div>

                  {TURNSTILE_SITE_KEY && (
                    <div data-testid="demo-turnstile-wrapper">
                      <div ref={turnstileRef} data-testid="demo-turnstile-widget" className="min-h-[65px]" />
                      {errors.turnstile && <p data-testid="demo-turnstile-error" className="mt-2 text-[12px] text-rose-600">{errors.turnstile}</p>}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-1">
                    <button type="submit" data-testid="demo-submit-button" disabled={submitting} className="btn btn-primary group disabled:opacity-60 disabled:cursor-not-allowed">
                      {submitting ? d.submitting : d.submit}
                      {!submitting && (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover:translate-x-0.5 transition-transform">
                          <ArrowRight size={13} strokeWidth={2.4} />
                        </span>
                      )}
                    </button>
                    <div className="inline-flex items-center gap-2 text-[11.5px] text-textsoft font-medium">
                      <ShieldCheck size={13} strokeWidth={1.8} className="text-green" />
                      {d.privacy}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
